/**
 * PLM Result Entry header pieces ported from the G5 PLM form (2026-10-15):
 *
 *  · the four device drop-downs (PLM Scope, Stereo Microscope, Hot Plate,
 *    Thermohydrometer) — each lists the PLM Instrument Setup rows with FIVE
 *    columns, Station first. Picking a row fills the other three devices and the
 *    layer's station from that same row (a device can serve two stations, so the
 *    row is used, not the value). Nothing is filled in from the QC batch's
 *    instrument — the analyst picks. Without setup rows each offers its
 *    remembered names (dbo.AI_PlmUtility, 2026-10-16).
 *    Pick-only (owner 2026-10-19, G5 parity: DropDown → DropDownList, "Combos
 *    should not be editable"): a device is always a listed row / name — never
 *    typed free text; it may be cleared.
 *  · the Daily QC ID picker — PASSED reads only; picking a read of an earlier
 *    day asks "You are selecting a past Daily QC ID. Do you want to continue?",
 *    and Cancel clears the box.
 *  · the Contamination Check box and its 50-layer badge (PLM Exam toolbar).
 */
import type { ReactNode } from 'react';
import { LookupCombo } from '../../../components/LookupCombo';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import type { PlmContamState, PlmDailyQcChoice, PlmObservationInput, PlmStationRow, PlmUtilityLists } from './plm.api';
import { dailyChoiceLabel } from './plmDailyQc';

/** The device fields, in the column order of the setup rows. */
export type PlmDeviceKey = 'microscopeId' | 'microscope' | 'hotPlate' | 'thermohydrometer';

const DEVICE_COLUMN: Record<PlmDeviceKey, number> = {
  microscopeId: 1, microscope: 2, hotPlate: 3, thermohydrometer: 4,
};

const DEVICE_COLS = ['Station', 'PLM Microscope', 'Stereo Microscope', 'Hot Plate', 'Thermohygrometer'];

/** The draft after a setup row was picked: its four devices and its station. */
export function applyStationRow(draft: PlmObservationInput, row: string[]): PlmObservationInput {
  const v = (i: number) => (row[i] ?? '').trim() || null;
  return {
    ...draft,
    station: v(0),
    microscopeId: v(1),
    microscope: v(2),
    hotPlate: v(3),
    thermohydrometer: v(4),
  };
}

export function PlmDeviceCombo({
  field, draft, rows, disabled, required, onChange,
}: {
  field: PlmDeviceKey;
  draft: PlmObservationInput;
  rows: PlmStationRow[];
  disabled: boolean;
  required?: boolean;
  onChange: (next: PlmObservationInput) => void;
}) {
  const col = DEVICE_COLUMN[field];
  const lookup = {
    cols: DEVICE_COLS,
    rows: rows.map(r => [r.station, r.plmMicroscope ?? '', r.stereoMicroscope ?? '', r.hotPlate ?? '', r.thermohygrometer ?? '']),
  };
  const value = draft[field] ?? '';
  return (
    <LookupCombo
      lookup={lookup}
      value={value}
      disabled={disabled}
      placeholder=""
      popupWidth={620}
      className={required && !value.trim() ? 'is-reqempty' : undefined}
      onPick={row => { if (row[col] !== undefined) onChange(applyStationRow(draft, row)); }}
      id={`plm-dev-${field}`}
    />
  );
}

/** The remembered list (dbo.AI_PlmUtility) of each device field (G5 parity 2026-10-16). */
const DEVICE_LIST: Record<PlmDeviceKey, Exclude<keyof PlmUtilityLists, 'oil'>> = {
  microscopeId: 'plmScope', microscope: 'stereoMicroscope', hotPlate: 'hotPlate', thermohydrometer: 'thermohydrometer',
};

/** The names a device box offers when no PLM Instrument Setup rows exist. */
export function deviceNames(field: PlmDeviceKey, lists: PlmUtilityLists | null | undefined): string[] {
  return lists?.[DEVICE_LIST[field]] ?? [];
}

/**
 * A device box without PLM Instrument Setup rows: PICK-ONLY from the remembered
 * names (owner 2026-10-19, G5 parity: the device combos became DropDownList). A
 * native select — type-ahead jumps through the list, the blank option clears, and
 * a stored value no longer remembered stays shown (and kept) until changed.
 */
export function PlmDevicePick({
  id, value, names, disabled, className, ariaLabel, onChange,
}: {
  id: string;
  value: string | null | undefined;
  names: readonly string[];
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  onChange: (value: string | null) => void;
}) {
  const options = devicePickOptions(names, value);
  return (
    <select
      id={id}
      className={`plm-input${className ? ` ${className}` : ''}`}
      aria-label={ariaLabel}
      value={value ?? ''}
      disabled={disabled}
      onChange={e => onChange(e.target.value || null)}
    >
      <option value="" />
      {options.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

/** The names a pick-only device box lists: the remembered ones, and the stored
 *  value first when it is no longer among them (owner 2026-10-19, G5 parity). */
export function devicePickOptions(names: readonly string[], value: string | null | undefined): string[] {
  const list = names.filter(n => n.trim() !== '');
  return value && value.trim() && !list.includes(value) ? [value, ...list] : list;
}

/**
 * A free-typing box with a drop-down of remembered names — G5's RI Oil
 * `<datalist>` and its device drop-downs without PLM Instrument Setup. A name
 * typed that is not listed is kept as typed and added to the list on Save
 * (server side, dbo.AI_PlmUtility).
 */
export function PlmNameInput({
  id, value, names, disabled, className, ariaLabel, placeholder, onChange,
}: {
  id: string;
  value: string | null | undefined;
  names: readonly string[];
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  placeholder?: string;
  onChange: (value: string | null) => void;
}) {
  const listId = `${id}-list`;
  return (
    <>
      <input
        type="text"
        id={id}
        className={`plm-input${className ? ` ${className}` : ''}`}
        aria-label={ariaLabel}
        placeholder={placeholder}
        list={names.length > 0 ? listId : undefined}
        autoComplete="off"
        value={value ?? ''}
        disabled={disabled}
        onChange={e => onChange(e.target.value || null)}
      />
      {names.length > 0 && (
        <datalist id={listId} data-testid={listId}>
          {names.map(n => <option key={n} value={n} />)}
        </datalist>
      )}
    </>
  );
}

/** yyyy-MM-dd of a server date, without a UTC shift. */
const dayOf = (iso: string | null | undefined) => (iso ?? '').slice(0, 10);

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const PAST_DAILY_QC_MESSAGE = 'You are selecting a past Daily QC ID. Do you want to continue?';

export function PlmDailyQcPicker({
  value, choices, disabled, required, onChange,
}: {
  value: string | null;
  choices: PlmDailyQcChoice[];
  disabled: boolean;
  required: boolean;
  onChange: (dailyQcId: string | null) => void;
}) {
  const { confirm } = useConfirm();
  const lookup = {
    cols: ['Daily QC ID', 'Date', 'Reference Slide ID', 'Analyst'],
    // A pending read (Daily QC optional, 2026-10-17) is listed "DQC-… (pending)".
    rows: choices.map(c => [dailyChoiceLabel(c), fmtLocalDate(c.qcDate), c.referenceId ?? '', c.analystName ?? '']),
  };
  return (
    <LookupCombo
      lookup={lookup}
      value={value ?? ''}
      disabled={disabled}
      placeholder=""
      popupWidth={520}
      className={required && !value ? 'is-reqempty' : undefined}
      id="plm-daily-qc-id"
      onPick={async row => {
        const picked = choices.find(c => dailyChoiceLabel(c) === row[0]);
        const id = picked?.dailyQcId ?? (row[0] || null);
        if (picked && dayOf(picked.qcDate) < todayLocal()) {
          const ok = await confirm({ title: 'Daily QC ID', message: PAST_DAILY_QC_MESSAGE, confirmLabel: 'OK' });
          // Cancel just clears the box — nothing is picked for the analyst.
          onChange(ok ? id : null);
          return;
        }
        onChange(id);
      }}
    />
  );
}

/** The Contamination Check box and its badge, for the PLM Exam toolbar. */
/**
 * The contamination-check status line — prototype v16 `renderContam`, word for word,
 * with the same bold parts and v17's tooltip (the full line, since it can be cut off
 * on a narrow screen). Green = checked, or layers 1–40 since the last check (with
 * when / who / which layer); amber = 41–50, or no check on record yet; red = 51st+.
 * The tone and the last check come from the server (PlmStationRules.ContamState).
 */
export function contamLine(state: PlmContamState | null, checked: boolean):
  { tone: string; line: ReactNode; full: string } {
  const limit = state?.limit ?? 50;
  const st = state?.station ?? null;
  const n = state?.position ?? 0;
  if (checked) {
    const t = '✓ Checked on this layer — count restarts (who and when are stamped on Save)';
    return { tone: 'checked', line: t, full: t };
  }
  if (!st) {
    return {
      tone: 'na',
      line: <>Select the <b>PLM Scope</b> to see the {limit}-layer contamination-check count.</>,
      full: `Select the PLM Scope to see the ${limit}-layer contamination-check count.`,
    };
  }
  const tone = state?.tone ?? 'ok';
  if (n > limit) {
    return {
      tone,
      line: <>✗ <b>{st}</b> · layer <b>{n}</b> since the last contamination check — tick <b>Contamination Check</b> on this layer to save</>,
      full: `✗ ${st} · layer ${n} since the last contamination check — tick Contamination Check on this layer to save`,
    };
  }
  if (!state?.lastCheckAt) {
    return {
      tone,
      line: <><b>{st}</b> · no contamination check recorded yet — layer <b>{n}</b> of {limit}</>,
      full: `${st} · no contamination check recorded yet — layer ${n} of ${limit}`,
    };
  }
  if (n === limit) {
    return {
      tone,
      line: <><b>{st}</b> · layer <b>{n}</b> of {limit} — this is the last layer before a check is required</>,
      full: `${st} · layer ${n} of ${limit} — this is the last layer before a check is required`,
    };
  }
  if (n > limit - 10) {
    const k = limit - n;
    const s2 = k === 1 ? '' : 's';
    return {
      tone,
      line: <><b>{st}</b> · layer <b>{n}</b> of {limit} — a contamination check is due within <b>{k}</b> layer{s2}</>,
      full: `${st} · layer ${n} of ${limit} — a contamination check is due within ${k} layer${s2}`,
    };
  }
  const at = fmtLocalDateTime(state.lastCheckAt);
  const by = state.lastCheckBy ?? '—';
  const layer = state.lastCheckLayer ?? '—';
  return {
    tone,
    line: <>✓ <b>{st}</b> · layer <b>{n}</b> of {limit} since the last contamination check — last checked <b>{at}</b> by <b>{by}</b> on <b>{layer}</b></>,
    full: `✓ ${st} · layer ${n} of ${limit} since the last contamination check — last checked ${at} by ${by} on ${layer}`,
  };
}

export function PlmContamControl({
  checked, state, disabled, onChange,
}: {
  checked: boolean;
  state: PlmContamState | null;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  const { tone, line, full } = contamLine(state, checked);
  return (
    <span className="plm-cc" data-testid="plm-contam">
      <label className="plm-chk" title="Tick after you clean the stage, tools and media and run the contamination check (one at least every 50 layers per station).">
        <input type="checkbox" checked={checked} disabled={disabled}
          aria-label="Contamination Check"
          onChange={e => onChange(e.target.checked)} />
        <span>Contamination Check</span>
      </label>
      <span className={`plm-cc__badge is-${tone}`} data-testid="plm-contam-badge"
        title={full}>
        {line}
      </span>
    </span>
  );
}
